import type {UseFormReturnType} from '@mantine/form';
import {BasicButton} from '@packages/ui/src';
import type {Dispatch, SetStateAction} from 'react';
import {z} from 'zod';
import {
  type QuestionFormStatus,
  questionFormStatus,
} from '../../../core/entities/box/constants';
import type {PostQuestionResponseType} from '../../../core/repositories/boxRepositoryTypes';
import {convertNlToBr} from '../../../shared/services/convertNlToBr';

export const QuestionFormInput = z.object({
  question: z
    .string()
    .min(1, '質問をご記入ください')
    .max(500, '文字数がオーバーしています'),
});
export type QuestionFormInput = z.infer<typeof QuestionFormInput>;
type Props = {
  status: QuestionFormStatus;
  isAdviserSelected: boolean;
  questionBody: string;
  setStatus: Dispatch<SetStateAction<QuestionFormStatus>>;
  onSubmit: (
    input: QuestionFormInput,
    e: unknown,
  ) => Promise<{
    status: QuestionFormStatus;
    res?: PostQuestionResponseType;
  }>;
  onError: (error: unknown, e: unknown) => void;
  form: UseFormReturnType<QuestionFormInput>;
};

export const QuestionForm: React.FC<Props> = ({
  status,
  isAdviserSelected,
  questionBody,
  setStatus,
  onSubmit,
  onError,
  form,
}) => {
  const questionValue = form.values.question;
  return (
    <form className="mt-20" onSubmit={form.onSubmit(onSubmit, onError)}>
      {status === questionFormStatus.add ? (
        <div
          className={`${
            form.errors.question ? 'border-notification-1' : ''
          } relative h-[257px] w-full rounded-[2px] border border-solid border-line-1 p-16 pb-32`}
        >
          <textarea
            id={'question'}
            {...form.getInputProps('question')}
            placeholder={`${
              isAdviserSelected ? '回答者' : '相談室'
            }に投げかけたい質問を投稿できます\n\n✓ 誤字脱字がないかご確認を!\n✓ 誹謗中傷や公序良俗に反する内容の場合、運営により削除される場合があります`}
            className={'h-full w-full resize-none text leading text-black'}
          />
          <p
            className={`${
              form.errors.question ? 'text-notification-1' : ''
            } absolute bottom-16 right-16 text-xs leading text-black-3`}
          >
            {questionValue.length}/500文字
          </p>
        </div>
      ) : (
        <>
          <div className="bg-background-1 p-16">
            <p className="text leading text-black-1">
              {convertNlToBr(questionBody)}
            </p>
          </div>
          <div className="mt-16">
            <p className="text-xs leading text-black-2">
              ✓ 誤字脱字がないかご確認を!
              <br />✓
              誹謗中傷や公序良俗に反する内容の場合、運営により削除される場合があります
            </p>
          </div>
        </>
      )}

      {status === questionFormStatus.add && form.errors.question ? (
        <p className={'mt-8 text-xs text-notification-1'}>
          {form.errors.question}
        </p>
      ) : (
        <></>
      )}

      <div className="mt-16 flex justify-center">
        <BasicButton
          label="質問を投稿する"
          type="submit"
          className={'w-full max-w-[340px]'}
          isPrimary={false}
          isLarge={true}
          isDisabled={false}
        />
      </div>

      {status !== questionFormStatus.add && (
        <div className="mt-16 flex justify-center">
          <button
            className="text leading text-primary-1 pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out"
            type="button"
            onClick={() => {
              setStatus(questionFormStatus.add);
            }}
          >
            戻る
          </button>
        </div>
      )}
    </form>
  );
};
